import { useMemo } from 'react';
import type { SslEqBand, TrackState } from '@melodinai/types';
import { useMixerStore } from '../../stores/mixerStore';

const BANDS: { id: SslEqBand['id']; label: string; min: number; max: number; defFreq: number }[] = [
  { id: 'high', label: 'High', min: 8000, max: 20000, defFreq: 12000 },
  { id: 'highMid', label: 'High Mid', min: 1000, max: 8000, defFreq: 3000 },
  { id: 'lowMid', label: 'Low Mid', min: 200, max: 2000, defFreq: 500 },
  { id: 'low', label: 'Low', min: 20, max: 500, defFreq: 120 },
];

const EQ_IDX: Record<SslEqBand['id'], number> = {
  low: 1,
  lowMid: 2,
  highMid: 4,
  high: 6,
};

function readBand(track: TrackState, id: SslEqBand['id']): SslEqBand {
  const eq = track.fxChain?.find((f) => f.type === 'EQ8');
  const bands = (eq?.params?.bands as Array<{ freq?: number; gain?: number; Q?: number }> | undefined) || [];
  const b = bands[EQ_IDX[id]];
  const meta = BANDS.find((x) => x.id === id)!;
  return {
    id,
    freq: b?.freq ?? meta.defFreq,
    gain: b?.gain ?? 0,
    Q: b?.Q ?? 0.707,
  };
}

function eqPath(bands: SslEqBand[], w: number, h: number): string {
  const pts: string[] = [];
  for (let x = 0; x <= w; x += 4) {
    const t = x / w;
    const freq = 20 * Math.pow(1000, t);
    let gain = 0;
    bands.forEach((b) => {
      const ratio = freq / Math.max(20, b.freq);
      const oct = Math.log2(Math.max(0.001, ratio));
      const q = Math.max(0.1, b.Q);
      const wOct = 1 / q;
      const env = Math.exp(-0.5 * (oct / wOct) ** 2);
      gain += b.gain * env;
    });
    const y = h / 2 - (gain / 18) * (h / 2);
    pts.push(`${x},${Math.max(0, Math.min(h, y))}`);
  }
  return `M ${pts.join(' L ')}`;
}

interface SslEqSectionProps {
  track: TrackState;
}

export function SslEqSection({ track }: SslEqSectionProps) {
  const setEqBand = useMixerStore((s) => s.setEqBand);
  const bands = useMemo(() => BANDS.map((b) => readBand(track, b.id)), [track]);
  const path = useMemo(() => eqPath(bands, 260, 72), [bands]);

  return (
    <section className="space-y-2 border-b border-ssl-metal/50 pb-3">
      <h3 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ssl-accent">EQ</h3>
      <svg viewBox="0 0 260 72" className="h-16 w-full rounded bg-black/40" aria-hidden>
        <line x1="0" y1="36" x2="260" y2="36" stroke="#3a3f48" strokeWidth="1" />
        <path d={path} fill="none" stroke="#3dff9a" strokeWidth="1.5" />
      </svg>
      <div className="space-y-2">
        {BANDS.map((meta) => {
          const b = bands.find((x) => x.id === meta.id)!;
          return (
            <div key={meta.id} className="rounded border border-ssl-metal/40 bg-black/20 p-2">
              <div className="mb-1 text-[9px] font-medium uppercase text-ssl-text">{meta.label}</div>
              <label className="mb-1 flex items-center gap-2 text-[9px] text-ssl-muted">
                <span className="w-8">Freq</span>
                <input
                  type="range"
                  min={meta.min}
                  max={meta.max}
                  step={1}
                  value={b.freq}
                  onChange={(e) => setEqBand(track.id, meta.id, { freq: Number(e.target.value) })}
                  className="h-1 flex-1 accent-ssl-led"
                />
                <span className="w-12 tabular-nums text-right">{Math.round(b.freq)} Hz</span>
              </label>
              <label className="mb-1 flex items-center gap-2 text-[9px] text-ssl-muted">
                <span className="w-8">Gain</span>
                <input
                  type="range"
                  min={-18}
                  max={18}
                  step={0.1}
                  value={b.gain}
                  onChange={(e) => setEqBand(track.id, meta.id, { gain: Number(e.target.value) })}
                  className="h-1 flex-1 accent-ssl-accent"
                />
                <span className="w-12 tabular-nums text-right">{b.gain.toFixed(1)} dB</span>
              </label>
              <label className="flex items-center gap-2 text-[9px] text-ssl-muted">
                <span className="w-8">Q</span>
                <input
                  type="range"
                  min={0.1}
                  max={10}
                  step={0.05}
                  value={b.Q}
                  onChange={(e) => setEqBand(track.id, meta.id, { Q: Number(e.target.value) })}
                  className="h-1 flex-1 accent-ssl-text"
                />
                <span className="w-12 tabular-nums text-right">{b.Q.toFixed(2)}</span>
              </label>
            </div>
          );
        })}
      </div>
    </section>
  );
}
